import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { UntypedFormGroup, UntypedFormControl, Validators, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { CmElement, ContentType, CmTooltip, CmHelp } from '../shared/model/cm/managed-content.model';
import { GlobalConstants } from '../shared/global-constants';
import { StateRegion } from '../shared/model/state-region';
import { UserSessionService } from '../shared/service/user-session.service';
import { PhysicianMhs } from '../shared/model/session/physician-mhs';
import { AddressMhs } from '../shared/model/session/address-mhs';
import { FormUtils } from '../shared/helper/form-utils';
import { Notice } from '../shared/model/session/notice';
import { MhsService } from '../shared/service/mhs.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { AppInsightsApiService } from '../shared/service/app-insights-api.service';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { ViewportScroller } from '@angular/common';
import { WindowRef } from '../shared/service/window.service';
import { ModalService } from '../shared/service/modal.service';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';
import { ApplicantType } from '../shared/model/ui/applicant-type';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { FormControlUtilitiesService } from '../shared/service/form-control-utilities.service';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';
import { MonthPickerComponent } from '../month-picker/month-picker.component';
import { ApplicationService } from '../shared/service/application.service';
import { PartialSaveResp } from '../shared/model/partial-save-resp';
import { PartialSaveData } from '../shared/model/partial-save-data';
import { environment } from '@environment';
import { EEOI_APP_ROUTES } from '../shared/global-constants';

@Component({
  selector: 'app-physician-info',
  standalone: true,
  imports: [ReactiveFormsModule, ContinueCancelComponent, MonthPickerComponent],
  templateUrl: './physician-info.component.html',
  styleUrls: ['./physician-info.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PhysicianInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEmitter = new EventEmitter<void>();
  @Output() backEmitter = new EventEmitter<void>();
  @Output() val = new EventEmitter<boolean>();
  @Input() showBackButton: boolean = false;

  public environmentName: string;

  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  public innerWidth: number = window.innerWidth;
  public lastConsultReasonTooltipPosition: string = 'right';

  private cancelContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);

  public pageTitle: string = "About the Physician";

  public timeInMilliseconds: string;
  public physicianLine1Name: string = "physicianLine1";
  public physicianCityName: string = "city";
  public physicianZipCodeName: string = "physicianZipCode";
  public lastConsultReasonName: string = "lastConsultReason";
  public physicianFirstNameName: string = "physicianFirstName";
  public physicianLastNameName: string = "physicianLastName";
  public physicianPhoneNumberName: string = "phoneNumber";
  public physicianStateName: string = "physicianState";
  public physicianForeignStateName: string = "physicianForeignState";
  public lastConsultDateDayName: string = "lastConsultDateDayInput";
  public lastConsultDateMonthName: string = "lastConsultDateMonthInput";
  public lastConsultDateYearName: string = "lastConsultDateYearInput";
  public physicianFacilityName: string = "physicianFacility";
  public selectTextState: string = 'State';
  public formSubmitted: boolean = false;

  public readonly ADDRESS_PATTERN = GlobalConstants.ADDRESS_PATTERN;
  public readonly REASON_LAST_CONSULTED_PATTERN = GlobalConstants.REASON_LAST_CONSULTED_PATTERN;
  public maxLengthLine1 = GlobalConstants.MAX_LENGTH_LINE1;
  public formNumber: string = this.userSessionService.data.formNumber;
  public isGenericDynamicForm: boolean = this.userSessionService.data.isGenericDynamicForm;
  public radioButtonOptions = [
    { name: 'physician', label: 'Physician' },
    { name: 'facility', label: 'Medical Facility' },
    { name: 'none', label: 'None' },
  ];

  physicianErrorThrown: boolean = false;//flag to only throw the warning message one time
  private isRestoring: boolean = false; // Track if we're currently restoring data
  public isEditFromReview: boolean = false;
  public isSavingEdit: boolean = false;
  /** Set to true when the user clicks Back without saving so ngOnDestroy
   *  does not overwrite the session cache with unsaved (possibly empty) form data. */
  private discardFormDataOnDestroy: boolean = false;

  public continueButtonProps = {
    keyId: 'continue-button',
    size: 'large',
    text: 'Continue',
    type: 'submit',
    variant: 'primary'
  }

  @Input() parentForm: UntypedFormGroup;
  @Input() label: string;

  physicianModalContent: Notice;

  public today = new Date();
  public readonly startConsultDate= new Date((this.today.getFullYear()+GlobalConstants.MONTH_PICKER_MIN_DATE_YEAR_OFFSET), GlobalConstants.MONTH_PICKER_MIN_DATE_MONTH_OFFSET, GlobalConstants.MONTH_PICKER_MIN_DATE_DAY_OFFSET);
  
  public readonly NAME_PATTERN = GlobalConstants.NAME_PATTERN;

  public taxIdMask = GlobalConstants.SOCIAL_SECURITY_PATTERN;
  public phoneMask = GlobalConstants.PHONE_MASK;
  public readonly US_STATES = GlobalConstants.US_STATES;
  public zipMask = GlobalConstants.ZIP_CODE_MASK;
  public readonly CA_PROVINCES = GlobalConstants.CANADIAN_PROVINCES;
  public readonly COUNTRIES = GlobalConstants.COUNTRIES;
  public countryList: Array<StateRegion> = this.COUNTRIES;
  public stateRegionList: Array<StateRegion> = this.US_STATES;
  public maxLengthCity = GlobalConstants.MAX_LENGTH_CITY;
  public maxLengthState = GlobalConstants.MAX_LENGTH_STATE;
  public maxLengthStateCd = GlobalConstants.MAX_LENGTH_STATE_CD;
  public maxLengthZip = GlobalConstants.MAX_LENGTH_ZIP;
  public maxLengthZipNonUs = GlobalConstants.MAX_LENGTH_ZIP_NON_US;
  public maxLengthPhone = GlobalConstants.PHONE_MAX_LENGTH_US;
  public maxLengthName = GlobalConstants.MAX_LENGTH_GENERIC_TREATMENT;

  public stateOfResidence: string;
  public lastConsultReasonPlaceholder: string;
  public lastConsultReasonLabel: string;

  public tooltipConsultReasonKeyId: string;
  public tooltipConsultReasonHeading: string;
  public tooltipConsultReasonDescription: string;

  public isPhysicianValid: boolean = true;

  physicianInfoForm: UntypedFormGroup;

  //Display items
  hasPhysicianElement: CmElement;
  physicianFirstNameElement: CmElement;
  physicianLastNameElement: CmElement;
  medicalFacilityElement: CmElement;
  phoneElement: CmElement;
  addressElement: CmElement;
  cityElement: CmElement;
  stateElement: CmElement;
  zipElement: CmElement;
  countryElement: CmElement;
  lastConsultDateElement: CmElement;
  lastVisitDateElement: CmElement;
  lastConsultReasonElement: CmElement;
  lastConsultReasonNonHivElement: CmElement;
  lastConsultReasonTooltip: CmTooltip;

  foreignAddress: boolean = false;
  public submitted: boolean;
  private subscriptions: Subscription[] = [];
  public countryLabels  = [];
  public stateRegionListLabels  = [];
  // GA4 Custom events
  public submitPhysicianInfo = {
    action: "button_click",
    category: "physician-info",
    name: "submit",
  }

  public cancelPhysicianInfo = {
    action: "button_click",
    category: "physician-info",
    name: "cancel",
  }

  public physicianInfoProvided = {
    action: "physician-info",
    category: "physician-check",
    name: "physician-info"
  };

  public facilityInfoProvided = {
    action: "facility-info",
    category: "physician-check",
    name: "facility-info"
  };

  public noInfoProvided = {
    action: "none",
    category: "physician-check",
    name: "none"
  };

  public physicianInfoProvidedAfterPrompt = {
    action: "physician-info-prompted",
    category: "physician-check",
    name: "physician-info-prompted"
  };

  public facilityInfoProvidedAfterPrompt = {
    action: "facility-info-prompted",
    category: "physician-check",
    name: "facility-info-prompted"
  };

  public noInfoProvidedAfterPrompt= {
    action: "none-prompted",
    category: "physician-check",
    name: "none-prompted"
  };

  constructor(private router: Router,
    private activatedRoute: ActivatedRoute,
    private cmService: ManagedContentService,
    private userSessionService: UserSessionService,
    private el: ElementRef,
    private googleAnalyticsService: GoogleAnalyticsService,
    private mhsService: MhsService,
    private fb: FormBuilder,
    private authGuardService: AuthGuardService,
    private appInsightsApiService: AppInsightsApiService,
    private scroller: ViewportScroller,
    private modalService: ModalService,
    private windowService: WindowRef,
    private appInsightsLoggerService: AppInsightsLoggerService,
    public formControlUtilitiesService: FormControlUtilitiesService,
    private cdr: ChangeDetectorRef,
    private applicationService: ApplicationService) {
  }

  get phoneNumber() { return this.physicianInfoForm.get("phoneNumber"); }
  ngOnInit() {
    if(this.userSessionService.data.sessionEnded){
      this.router.navigate(['/instructions']);
    }

    // Detect edit-mode entry from the review Overview "View" button.
    // Mirrors the same detection used by applicant-info and med-history so the page
    // can show Back / Save buttons and persist changes via a partial update
    // instead of the standard Continue / Cancel flow.
    const isEditFromState = history.state && history.state.editPhysician === true;
    const isEditFromQuery = this.activatedRoute.snapshot.queryParamMap.get('mode') === 'edit';
    this.isEditFromReview = isEditFromState || isEditFromQuery;
    
    // Clear the query params from the URL after detection to keep URL clean
    if (isEditFromQuery) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {},
        replaceUrl: true
      });
    }

    // Reset form submission state when navigating back to this page
    this.formSubmitted = false;
    this.submitted = false;

    // Check if modal has been shown and acknowledged in this session
    // If user already acknowledged the modal, don't reset the flag
    const modalAcknowledged = this.userSessionService.data['physicianModalAcknowledged'];
    if (modalAcknowledged) {
      this.physicianErrorThrown = true; // Keep flag set to prevent showing modal again
    } else {
      this.physicianErrorThrown = false;
    }
    this.environmentName = environment.name;

    this.setPageTitle();

    let today = new Date();
    this.timeInMilliseconds = today.getTime().toString();

    this.hasPhysicianElement = this.cmService.getContentById(ContentType.ELEMENT, 'physician-records');
    this.physicianFirstNameElement = this.cmService.getContentById(ContentType.ELEMENT, 'physician-first-name');
    this.physicianLastNameElement = this.cmService.getContentById(ContentType.ELEMENT, 'physician-last-name');
    this.medicalFacilityElement = this.cmService.getContentById(ContentType.ELEMENT, 'physician-facility');
    this.phoneElement = this.cmService.getContentById(ContentType.ELEMENT, 'phone');
    this.addressElement = this.cmService.getContentById(ContentType.ELEMENT, 'address');
    this.cityElement = this.cmService.getContentById(ContentType.ELEMENT, 'city');
    this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state');
    this.zipElement = this.cmService.getContentById(ContentType.ELEMENT, 'zip');
    this.countryElement = this.cmService.getContentById(ContentType.ELEMENT, 'country');

    if(this.isGenericDynamicForm) {
      this.lastVisitDateElement = this.cmService.getContentById(ContentType.ELEMENT,'date-last-visit');
    } else {
      this.lastConsultDateElement = this.cmService.getContentById(ContentType.ELEMENT, 'date-last-consulted');
      this.lastConsultReasonElement = this.cmService.getContentById(ContentType.ELEMENT, 'reason-last-consulted');
    }

    this.lastConsultReasonElement = this.cmService.getContentById(ContentType.ELEMENT, 'reason-last-consulted');
    this.lastConsultReasonNonHivElement = this.cmService.getContentById(ContentType.ELEMENT, 'reason-last-consulted-exclude-hiv');

    this.lastConsultReasonTooltip = this.cmService.getContentById(ContentType.TOOLTIP, "last-consult-reason");
    if(this.lastConsultReasonTooltip){
      this.tooltipConsultReasonKeyId = 'helpLastConsultReason';
      this.tooltipConsultReasonHeading = this.lastConsultReasonTooltip.title;
      this.tooltipConsultReasonDescription = this.lastConsultReasonTooltip.plainText;
    }

    let physicianWarning = this.cmService.getContentById(ContentType.HELP,"physician-warning");
     this.physicianModalContent = {
      id: physicianWarning.id,
      title: physicianWarning.title,
      body: physicianWarning.body
    };

    // Check for saved form data in session
    const savedPhysicianData = this.userSessionService.data['physicianFormData'];

    // Check if form already exists in parent (user navigated back to this page)
    if (this.parentForm && this.parentForm.contains('physician')) {
      // Use existing form to preserve data
      this.physicianInfoForm = this.parentForm.get('physician') as UntypedFormGroup;
    } else {
      // Create new form for first visit
      this.physicianInfoForm = new UntypedFormGroup({
        hasPhysician: new UntypedFormControl(null, [Validators.required])
      });
    }

    this.physicianInfoForm.updateValueAndValidity();

    this.stateOfResidence = this.userSessionService.data.state;
    if(!this.isGenericDynamicForm){
      this.lastConsultReasonPlaceholder = this.lastConsultReasonElement.placeholder;
      this.lastConsultReasonLabel = this.lastConsultReasonElement.label;
    }
    if(this.checkExcludingHivState(this.stateOfResidence) && !this.isGenericDynamicForm){
      this.lastConsultReasonPlaceholder = this.lastConsultReasonNonHivElement.placeholder;
      this.lastConsultReasonLabel = this.lastConsultReasonNonHivElement.label;
    }

    this.today.setDate(this.today.getDate());

    this.countryLabels = [this.countryElement?.label].concat(this.countryList.map(country => country.label)); 
    this.stateRegionListLabels = [this.selectTextState].concat(this.stateRegionList.map(stateRegion => stateRegion.label));

    //code that was previously in ngAfterInitView below

    // Add form to parent if it was newly created
    const wasNewlyCreated = this.parentForm && !this.parentForm.contains('physician');
    if(wasNewlyCreated) {
      this.parentForm.addControl('physician', this.physicianInfoForm);
    }

    // Restore saved data if it exists (whether form is new or reused)
    if (savedPhysicianData) {
      this.restoreFormData(savedPhysicianData);
    }

    if(this.mhsService){
      this.mhsService.submitComponent.subscribe(msg => {
        if(msg){
          this.onSubmit();
        }
      });
    }

    if(this.mhsService){
      const sub1 = this.mhsService.submitPhysician.subscribe(msg => {
        if(msg){
          this.onSubmit();
        }
      });
      this.subscriptions.push(sub1);
    }

    if(this.mhsService){
      const sub2 = this.mhsService.throwPhysicianErrorMessage.subscribe(msg => {
        if(msg){
          this.throwPhysicianError();
        }
      });
      this.subscriptions.push(sub2);
    }

    if(this.windowService){
    this.windowService.nativeDocument
      .querySelector('body')
      .addEventListener('click', (event: Event) => this.onClick(event));
    }

    this.physicianInfoForm.get('hasPhysician').valueChanges.subscribe(val => {
      if(val == 'physician'){
        //add controls
        this.physicianInfoForm.addControl('physicianFirstName', this.fb.control('', []));//validators are controlled through the leave event
        this.physicianInfoForm.addControl('physicianLastName', this.fb.control('', []));//validators are controlled through the leave event

        this.physicianInfoForm.addControl('phoneNumber', this.fb.control('', [Validators.required, Validators.pattern(GlobalConstants.PHONE_REGEX)]));
        this.physicianInfoForm.addControl('country', this.fb.control('US', [Validators.required]));
        this.physicianInfoForm.addControl('physicianLine1', this.fb.control('', [Validators.required, Validators.maxLength(this.maxLengthLine1), Validators.pattern(this.ADDRESS_PATTERN)]));
        this.physicianInfoForm.addControl('city', this.fb.control('', []));//validators are controlled through the leave event
        this.physicianInfoForm.addControl('physicianState', this.fb.control('', [Validators.required]));
        this.physicianInfoForm.addControl('physicianZipCode', this.fb.control('', [Validators.required, Validators.pattern(GlobalConstants.POSTAL_CODE_PATTERN_US)]));
        if(!this.isGenericDynamicForm){
          this.physicianInfoForm.addControl('lastConsultReason', this.fb.control('', [Validators.required, Validators.maxLength(GlobalConstants.MAX_LENGTH_GENERIC_TREATMENT), Validators.pattern(this.REASON_LAST_CONSULTED_PATTERN)]));
        }

        this.zipElement = this.cmService.getContentById(ContentType.ELEMENT, 'zip');
        if(!this.isGenericDynamicForm){
          this.physicianInfoForm.addControl('lastConsultReason', this.fb.control('', [Validators.required, Validators.maxLength(GlobalConstants.MAX_LENGTH_GENERIC_TREATMENT)]));     
          this.physicianInfoForm.addControl('lastConsultDateDay', this.fb.control('', []));
          this.physicianInfoForm.addControl('lastConsultDateMonth', this.fb.control('', []));
          this.physicianInfoForm.addControl('lastConsultDateYear', this.fb.control('', []));
        } else {
          this.physicianInfoForm.addControl('lastConsultDate', this.fb.control('', [Validators.required]));
        }

        //remove unused controls
        this.physicianInfoForm.removeControl('medicalFacility');

        this.physicianInfoForm.updateValueAndValidity();

      } else if(val == 'facility'){
        //add controls
        this.physicianInfoForm.addControl('medicalFacility', this.fb.control('', [Validators.required, Validators.maxLength(100)]));

        this.physicianInfoForm.addControl('phoneNumber', this.fb.control('', [Validators.required, Validators.pattern(GlobalConstants.PHONE_REGEX)]));
        this.physicianInfoForm.addControl('country', this.fb.control('US', [Validators.required]));
        this.physicianInfoForm.addControl('physicianLine1', this.fb.control('', [Validators.required, Validators.maxLength(150)]));
        this.physicianInfoForm.addControl('city', this.fb.control('', [Validators.required, Validators.maxLength(50)]));
        this.physicianInfoForm.addControl('physicianState', this.fb.control('', [Validators.required]));
        this.physicianInfoForm.addControl('physicianZipCode', this.fb.control('', [Validators.required, Validators.pattern(GlobalConstants.POSTAL_CODE_PATTERN_US)]));
        if(!this.isGenericDynamicForm){
          this.physicianInfoForm.addControl('lastConsultReason', this.fb.control('', [Validators.required, Validators.maxLength(150)]));
        }

        this.zipElement = this.cmService.getContentById(ContentType.ELEMENT, 'zip');
        if(!this.isGenericDynamicForm){
          this.physicianInfoForm.addControl('lastConsultReason', this.fb.control('', [Validators.required, Validators.maxLength(150)]));  
          this.physicianInfoForm.addControl('lastConsultDateDay', this.fb.control('', []));
          this.physicianInfoForm.addControl('lastConsultDateMonth', this.fb.control('', []));
          this.physicianInfoForm.addControl('lastConsultDateYear', this.fb.control('', []));
        } else {
          this.physicianInfoForm.addControl('lastConsultDate', this.fb.control('', [Validators.required]));
        }

        //remove unused controls
        this.physicianInfoForm.removeControl('physicianFirstName');
        this.physicianInfoForm.removeControl('physicianLastName');

        this.physicianInfoForm.updateValueAndValidity();

      } else if(val == 'none'){
        //remove unused controls
        this.physicianInfoForm.removeControl('physicianFirstName');
        this.physicianInfoForm.removeControl('physicianLastName');
        this.physicianInfoForm.removeControl('medicalFacility');
        this.physicianInfoForm.removeControl('phoneNumber');
        this.physicianInfoForm.removeControl('country');
        this.physicianInfoForm.removeControl('physicianLine1');
        this.physicianInfoForm.removeControl('city');
        this.physicianInfoForm.removeControl('physicianState');
        this.physicianInfoForm.removeControl('physicianZipCode');
        this.physicianInfoForm.removeControl('lastConsultDateDay');
        this.physicianInfoForm.removeControl('lastConsultDateMonth');
        this.physicianInfoForm.removeControl('lastConsultDateYear');
        if(!this.isGenericDynamicForm){
          this.physicianInfoForm.removeControl('lastConsultReason');
        }
        this.physicianInfoForm.removeControl('lastConsultDate');
        // Also remove month-picker controls for generic dynamic form
        this.physicianInfoForm.removeControl('physicianLastVisitMonthName');
        this.physicianInfoForm.removeControl('physicianLastVisitYearName');

        this.physicianInfoForm.updateValueAndValidity();
      }
    });

    this.disableAutoFill();

    this.resizeObservable$ = fromEvent(window, 'resize')
    this.resizeSubscription$ = this.resizeObservable$.subscribe( evt => {
      this.innerWidth = window.innerWidth;
      this.changeTooltipPosition();
    });

  }//end of ngOnInit

  ngAfterViewInit() {
    this.appInsightsApiService.endNavigationEvent(this.router.url);
    
    // Ensure formSubmitted stays false after view init
    this.formSubmitted = false;

    // Check if we need to restore data (browser back button case)
    const savedPhysicianData = this.userSessionService.data['physicianFormData'];
    
    // Check if we have saved data but it's not in the current form
    // This handles browser back button navigation where component is already mounted
    if (savedPhysicianData) {
      const savedKeys = Object.keys(savedPhysicianData);
      const currentKeys = Object.keys(this.physicianInfoForm.controls);
      
      // Check if there are keys in saved data that don't exist in current form
      const missingKeys = savedKeys.filter(key => !this.physicianInfoForm.get(key));
      
      if (missingKeys.length > 0) {
        this.restoreFormData(savedPhysicianData);
      }
    }

    setTimeout( ()=>{
      this.onScrollToTop();
    }, 1);

    this.innerWidth = window.innerWidth;
    this.changeTooltipPosition();
  }

  ngOnDestroy() {
    // Save form data FIRST before unsubscribing.
    // Skip if the user clicked Back without saving to prevent overwriting the
    // session cache with unsaved (possibly empty) form data.
    if (!this.discardFormDataOnDestroy) {
      this.saveFormData();
    }
    
    // Then unsubscribe from all subscriptions to prevent memory leaks and duplicate calls
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private saveFormData(): void {
    if (this.physicianInfoForm) {
      const formValue = this.physicianInfoForm.getRawValue(); // Use getRawValue to get all fields including disabled ones
      
      // Don't save if hasPhysician is null and the error modal was thrown
      // This prevents saving the temporary null state when the modal is shown
      if (formValue.hasPhysician === null && this.physicianErrorThrown) {
        return;
      }
      
      const hasData = Object.values(formValue).some(value => 
        value !== null && value !== undefined && value !== ''
      );
      
      if (hasData) {
        // Check if current data has fewer keys than saved data
        // This prevents overwriting good data with incomplete data during component recreation
        const currentSavedData = this.userSessionService.data['physicianFormData'];
        const currentKeyCount = Object.keys(formValue).length;
        const savedKeyCount = currentSavedData ? Object.keys(currentSavedData).length : 0;
        
        // Check if the hasPhysician radio button selection has changed
        const currentSelection = formValue.hasPhysician;
        const savedSelection = currentSavedData?.hasPhysician;
        const selectionChanged = currentSelection !== savedSelection;
        
        // Save if:
        // 1. We have more or equal keys than before (normal case)
        // 2. OR the radio button selection has changed (user changed from physician/facility/none)
        //    In this case, fewer keys is expected and intentional
        if (currentKeyCount >= savedKeyCount || selectionChanged) {
          this.userSessionService.data['physicianFormData'] = formValue;
        }
      }
    }
  }

  private restoreFormData(savedPhysicianData: any): void {
    // Mark that we're restoring to prevent auto-submission
    this.isRestoring = true;
    this.submitted = false;
    
    // Set values synchronously first to prevent validation flash
    Object.keys(savedPhysicianData).forEach(key => {
      const control = this.physicianInfoForm.get(key);
      if (control && savedPhysicianData[key] !== null && savedPhysicianData[key] !== undefined) {
        control.setValue(savedPhysicianData[key], { emitEvent: false });
        // Mark as untouched and pristine immediately to prevent validation errors
        control.markAsUntouched();
        control.markAsPristine();
      }
    });
    
    setTimeout(() => {
      Object.keys(savedPhysicianData).forEach(key => {
        const control = this.physicianInfoForm.get(key);
        if (control && savedPhysicianData[key] !== null && savedPhysicianData[key] !== undefined) {
          control.setValue(savedPhysicianData[key], { emitEvent: true });
          // Mark as untouched and pristine to prevent validation errors from showing
          control.markAsUntouched();
          control.markAsPristine();
          control.updateValueAndValidity();
        } else if (!control && savedPhysicianData[key] !== null && savedPhysicianData[key] !== undefined) {
          // Control doesn't exist yet (e.g., month-picker controls like lastVisitDate-month, lastVisitDate-year)
          // Add it to the form so child components can find it
          this.physicianInfoForm.addControl(key, new UntypedFormControl(savedPhysicianData[key]));
        }
      });
      
      // CRITICAL: After restoring values with emitEvent:true, the valueChanges subscription
      // will have added the necessary controls. NOW we can call countryChange to set validators and foreignAddress
      if (savedPhysicianData['country']) {
        const savedCountry = savedPhysicianData['country'];
        // Call countryChange with preserveValues=true to set up validators without clearing state/zip
        this.countryChange(savedCountry, true);
      }
      
      // Mark entire form as untouched and pristine to prevent validation errors
      this.physicianInfoForm.markAsUntouched();
      this.physicianInfoForm.markAsPristine();
      
      this.cdr.detectChanges();
      // Force web components to update
      setTimeout(() => {
        this.forceWebComponentUpdate();
        this.cdr.detectChanges();
        // Clear the restoring flag after a delay to allow restoration to settle
        setTimeout(() => {
          this.isRestoring = false;
        }, 100);
      }, 50);
    }, 100);
  }

  onScrollToTop(){
    this.scroller.setOffset([0, 200]);
    this.scroller.scrollToAnchor('topOfPage');
  }

  changeTooltipPosition(){
    if(this.innerWidth < 505){
      this.lastConsultReasonTooltipPosition = 'left';
    } else {
      this.lastConsultReasonTooltipPosition = 'right';
    }
  }

  public setPageTitle(){
    if(this.userSessionService.data.applicantType){
      if(this.userSessionService.data.applicantType.toLowerCase() === ApplicantType.EMPLOYEE.toLowerCase()){
        this.pageTitle = "About the Physician (Employee)";
      } else if(this.userSessionService.data.applicantType.toLowerCase() === ApplicantType.SPOUSE.toLowerCase()){
        this.pageTitle = "About the Physician (Spouse)";
      } else if(this.userSessionService.data.applicantType.toLowerCase() === ApplicantType.CHILD.toLowerCase()){
        this.pageTitle = "About the Physician (Child)";
      }
    }
  }

  private forceWebComponentUpdate() {
    // Handle all prism-ui input component types
    const componentSelectors = [
      'prism-ui-input-text',
      'prism-ui-input-phone',
      'prism-ui-input-number',
      'prism-ui-radio-button',
      'prism-ui-form-field-select'
    ];

    componentSelectors.forEach(selector => {
      const inputs = document.querySelectorAll(selector);
      inputs.forEach((input: any) => {
        const formControlName = input.getAttribute('formControlName');
        if (formControlName) {
          const control = this.physicianInfoForm.get(formControlName);
          const controlValue = control?.value;
          // For radio buttons, always process to set checked/unchecked state
          if (selector === 'prism-ui-radio-button') {
            const inputValue = input.getAttribute('value');
            if (controlValue && inputValue === controlValue) {
              input.setAttribute('checked', 'true');
              input.checked = true;
            } else {
              // Explicitly uncheck other radio buttons in the same group
              input.removeAttribute('checked');
              input.checked = false;
            }
            return; // Skip the rest of the processing for radio buttons
          }
          
          if (control && controlValue !== null && controlValue !== undefined && controlValue !== '') {
            // Try to set the value on the web component's shadow DOM
            if (input.shadowRoot) {
              const innerInput = input.shadowRoot.querySelector('input');
              if (innerInput) {
                innerInput.value = controlValue;
                // Dispatch input event to trigger any listeners
                innerInput.dispatchEvent(new Event('input', { bubbles: true }));
              }
            }
            
            // Set the defaultValue property
            input.defaultValue = controlValue;
            
            // Also try setting the value property directly
            if (input.value !== controlValue) {
              input.value = controlValue;
            }
          }
        }
      });
    });
  }

  private disableAutoFill(){

    // Add autocomplete='off' to form elements
    Array.from(document.querySelectorAll('form'))
    .forEach(element => {
      element.setAttribute('autocomplete', 'off');
    });

    Array.from(document.querySelectorAll('input[matInput]'))
    .forEach(element => {
      // Set name equal to id if name attribute doesn't exist
      if(!element.getAttribute("name")){
        element.setAttribute('name', element.getAttribute("id")? element.getAttribute("id"): '');
      }
       // Prepend element names to make them appear unique
      if(element.getAttribute("name") && element.getAttribute("name").indexOf(this.timeInMilliseconds) === -1){
          element.setAttribute('name', this.timeInMilliseconds + "-" + element.getAttribute("name"));
      }

      // Set value of autocomplete attribute equal to element name
      // Modern browsers (particularly Chrome) don't honor autocomplete='off'
      element.setAttribute('autocomplete', element.getAttribute("name"));
    });

    this.physicianLine1Name = this.timeInMilliseconds + "-" + this.physicianLine1Name;
    this.physicianCityName = this.timeInMilliseconds + "-" + this.physicianCityName;
    this.physicianZipCodeName = this.timeInMilliseconds + "-" + this.physicianZipCodeName;
    this.lastConsultReasonName = this.timeInMilliseconds + "-" + this.lastConsultReasonName;
    this.physicianFirstNameName = this.timeInMilliseconds + "-" + this.physicianFirstNameName;
    this.physicianLastNameName = this.timeInMilliseconds + "-" + this.physicianLastNameName;
    this.physicianPhoneNumberName = this.timeInMilliseconds + "-" + this.physicianPhoneNumberName;
    this.physicianStateName = this.timeInMilliseconds + "-" + this.physicianStateName;
    this.physicianForeignStateName = this.timeInMilliseconds + "-" + this.physicianForeignStateName;
    this.lastConsultDateDayName = this.timeInMilliseconds + "-" + this.lastConsultDateDayName;
    this.lastConsultDateMonthName = this.timeInMilliseconds + "-" + this.lastConsultDateMonthName;
    this.lastConsultDateYearName = this.timeInMilliseconds + "-" + this.lastConsultDateYearName;
    this.physicianFacilityName = this.timeInMilliseconds + "-" + this.physicianFacilityName;
  }

  countryChange(country: string, preserveValues: boolean = false){
    // Defensive check: only proceed if the required controls exist
    if (!this.physicianInfoForm.get('physicianState') || 
        !this.physicianInfoForm.get('physicianZipCode') || 
        !this.physicianInfoForm.get('city')) {
      return;
    }

    if(country == 'US'){
      // Only clear values if not preserving (i.e., user is changing country, not restoring)
      if (!preserveValues) {
        this.physicianInfoForm.get("physicianState").setValue("");
        this.physicianInfoForm.get('physicianZipCode').setValue("");
      }
      this.physicianInfoForm.get('physicianZipCode').setValidators([
        Validators.required,
        Validators.maxLength(this.maxLengthZip),
        Validators.pattern(GlobalConstants.POSTAL_CODE_PATTERN_US)
      ]);
      this.physicianInfoForm.get('city').setValidators([
        Validators.required,
        Validators.maxLength(50)
      ]);
      this.physicianInfoForm.get('physicianState').setValidators([
        Validators.required
      ]);

      this.foreignAddress = false;
      this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state');
      this.zipElement = this.cmService.getContentById(ContentType.ELEMENT, 'zip');
    } else {
      this.foreignAddress = true;
      this.stateElement = this.cmService.getContentById(ContentType.ELEMENT, 'state-province');
      this.zipElement = this.cmService.getContentById(ContentType.ELEMENT, 'zip-postal');

      this.physicianInfoForm.get('physicianZipCode').setValidators([
        Validators.maxLength(this.maxLengthZipNonUs),
        Validators.pattern(GlobalConstants.POSTAL_CODE_PATTERN_NON_US)
      ]);
      this.physicianInfoForm.get('city').setValidators([
        Validators.maxLength(this.maxLengthCity),
          Validators.pattern("^(.*[^ ].*)$")
      ]);
      this.physicianInfoForm.get('physicianState').setValidators([
        Validators.maxLength(this.maxLengthState),
        Validators.pattern("^(.*[^ ].*)$")
      ]);
    }
    this.physicianInfoForm.get('physicianZipCode').updateValueAndValidity();
    this.physicianInfoForm.get('city').updateValueAndValidity();
    this.physicianInfoForm.get('physicianState').updateValueAndValidity();
  }

  public checkExcludingHivState(state: string): boolean{
    switch(state){
      case 'ME':
        return true;
    }
    return false;
  }

  onSubmit(){
    // Prevent submission during restoration to avoid auto-advancing
    if (this.isRestoring) {
      return;
    }
    
    // If formSubmitted is already true, prevent duplicate submissions
    if (this.formSubmitted) {
      return;
    }
    
    // CRITICAL: Check if hasPhysician is set before doing anything
    // If not set, mark as touched and return without showing spinner
    const hasPhysicianValue = this.physicianInfoForm.controls['hasPhysician']?.value;
    if (!hasPhysicianValue || hasPhysicianValue === null || hasPhysicianValue === undefined || hasPhysicianValue === '') {
      this.physicianInfoForm.markAllAsTouched();
      this.physicianInfoForm.updateValueAndValidity();
      return;
    }
    
    this.formSubmitted = true;
    const spinnerStartTime = new Date().getTime();
    this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Started.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
    
    this.touchAllControls();
    if(this.isGenericDynamicForm){
      this.setLastConsultDate();
    }
    
    let okToAdvanceToNextStep: boolean;
    if(this.physicianInfoForm.controls['hasPhysician'].value != 'none'){
      if(this.physicianInfoForm.valid){
        okToAdvanceToNextStep = true;
      } else {
        okToAdvanceToNextStep = false;
      }
    } else {
      if(this.physicianInfoForm.valid){
        // Show modal ONLY if:
        // 1. It hasn't been shown yet in this visit (physicianErrorThrown flag)
        // 2. User answered "yes" to at least one question requiring physician info in med-history
        if(!this.physicianErrorThrown && this.userSessionService.data.answeredYesToRequiredPhysicianQuestion){
          this.throwPhysicianError();
          okToAdvanceToNextStep = false;
        } else {
          // Mark as acknowledged since user is advancing (implicit acknowledgment)
          this.userSessionService.data['physicianModalAcknowledged'] = true;
          okToAdvanceToNextStep = true;
        }
      } else {
        okToAdvanceToNextStep = false;
      }
    }
    
    // Only show spinner if we're actually going to advance
    if(okToAdvanceToNextStep && this.physicianInfoForm.valid){
      this.formSubmitted = true;
    }

    if(okToAdvanceToNextStep){
      if(this.physicianInfoForm.valid){
        if(this.environmentName === 'demonstration'){
          this.fillPhysicianInfo();
          this.sendPhysicianAnalytics();
          this.submitted = true;
          this.isPhysicianValid = true;
          this.val.emit(this.submitted);
        } else {
          this.fillPhysicianInfo();
          this.sendPhysicianAnalytics();
          this.savePhysician(spinnerStartTime);
        }
      } else {
        this.isPhysicianValid = false;
        this.scrollToFirstInvalidControl();
        this.submitted = false;
        this.formSubmitted = false;
        this.cdr.detectChanges(); // Force view update
        this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Stopped.  There was an invalid control.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      }
    } else {
      // Stop spinner when not advancing to next step
      this.isPhysicianValid = false;
      this.scrollToFirstInvalidControl();
      this.submitted = false;
     
      // Not advancing to next step - ensure spinner is stopped
      this.formSubmitted = false;
      this.cdr.detectChanges(); // Force view update
      const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Stopped. Not advancing to next step.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
    }
    // Final safety check - ensure formSubmitted is false at end of method
    this.formSubmitted = false;
    this.cdr.detectChanges(); // Force view update
  }

  scrollToFirstInvalidControl(){
    var firstInvalidControlName: string = FormUtils.findFirstInvalidControl(this.physicianInfoForm);
    const firstInvalidControl = document.querySelector(`[formControlName="${firstInvalidControlName}"]`);
    
    if (firstInvalidControl) {
      var headerOffset = 170;
      var elementPosition = firstInvalidControl.getBoundingClientRect().top;
      var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  }

  async savePhysician(spinnerStartTime: number){
    let resp = new PartialSaveResp();
    
    try {
      const result = await this.applicationService.updatePhysicianWithExStreamCloudApplication(this.userSessionService.data.mhsId, this.userSessionService.data.mhsApplication.physician);
      resp.httpStatusCode = result.httpStatusCode;
      resp.message = result.message;
      resp.data = result.data;
      // 201 (Created) and 304 (Not Modified) are both successful responses
      if(resp.httpStatusCode === 201 || resp.httpStatusCode === 304){
        this.appInsightsLoggerService.log('info', 'Physician Info updated successfully. mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        this.submitted = true;
        this.isPhysicianValid = true;
        
        // In edit mode, navigate back to review instead of advancing to next step
        if (this.isEditFromReview) {
          this.isSavingEdit = false;
          this.formSubmitted = false;
          this.cdr.detectChanges();
          this.navigateBackToReviewAfterSave();
        } else {
          this.val.emit(this.submitted);
          this.formSubmitted = false;
        }
        
        this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      } else {
        this.submitted = false;
        this.isSavingEdit = false;
        this.appInsightsLoggerService.log('error', 'Physician Info update failed. mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        this.formSubmitted = false;
        this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      }
    } catch (error) {
      resp.httpStatusCode = error.error.httpStatusCode;
      resp.message = error.message;
      resp.data = error.error.data;
      this.findInvalidControls(resp.data);
      this.scrollToFirstInvalidControl();
      this.submitted = false;
      this.isSavingEdit = false;
      this.appInsightsLoggerService.log('error', 'Physician Info update failed. mhsId/caseId: ' + this.userSessionService.data.mhsId, error);
      this.formSubmitted = false;
      this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      const spinnerStopTime = new Date().getTime();
      const spinnerDuration = spinnerStopTime - spinnerStartTime;
      this.appInsightsLoggerService.log('info', 'Physician Info Component: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
    }
  }

  findInvalidControls(resp: PartialSaveData){
    for(const key in resp){
      if(key === "physician.physicianLine1"){
        this.physicianInfoForm.get('physicianLine1').setErrors({ pattern: resp[key] });
      } else if(key === "physician.city"){
        this.physicianInfoForm.get('city').setErrors({ pattern: resp[key] });
      } else if(key === "physician.zip"){
        this.physicianInfoForm.get('physicianZipCode').setErrors({ pattern: resp[key] });
      } else if(key === "physician.medicalFacility"){
        this.physicianInfoForm.get('medicalFacility').setErrors({ pattern: resp[key] });
      } else if(key === "physician.physicianFirstName"){
        this.physicianInfoForm.get('physicianFirstName').setErrors({ pattern: resp[key] });
      } else if(key === "physician.physicianLastName"){
        this.physicianInfoForm.get('physicianLastName').setErrors({ pattern: resp[key] });
      }
    }

    this.scrollToFirstInvalidControl();
  }

  public setLastConsultDate(){
    if(this.physicianInfoForm.controls['hasPhysician'].value != 'none' && this.isGenericDynamicForm){
      if(this.physicianInfoForm.controls['physicianLastVisitMonthName'].value != null && this.physicianInfoForm.controls['physicianLastVisitYearName'].value != null){
        let lastVisitMonth = this.physicianInfoForm.controls['physicianLastVisitMonthName'].value;
        let lastVisitYear = this.physicianInfoForm.controls['physicianLastVisitYearName'].value;
        this.physicianInfoForm.controls['lastConsultDate'].setValue(lastVisitYear + "-" + lastVisitMonth );
      }
    }
  }

  touchAllControls(){
    this.physicianInfoForm.markAllAsTouched();
    this.resetPhysicianError();
    if(this.physicianInfoForm.contains('physicianFirstName')){
      this.firstNameLeaveEvent();
    }
    if(this.physicianInfoForm.contains('physicianLastName')){
      this.lastNameLeaveEvent();
    }
    if(this.physicianInfoForm.contains('city')){
      let cityValue = this.physicianInfoForm.get('city').value;
      const cityEvent = {target: {value: cityValue}};
      this.cityLeaveEvent(cityEvent); 
    }
    if(this.physicianInfoForm.contains('lastConsultDateMonth')){
      this.lastConsultDateMonthLeaveEvent();
    }
    if(this.physicianInfoForm.contains('lastConsultDateYear')){
      this.lastConsultDateYearLeaveEvent();
    }
    if(this.physicianInfoForm.contains('lastConsultDateDay')){
      this.lastConsultDateDayLeaveEvent();
    }

    // Explicitly mark physician last visit month and year controls as touched
    // This ensures validation errors show in Firefox (and all browsers)
    if(this.physicianInfoForm.contains('physicianLastVisitMonthName')){
      this.physicianInfoForm.get('physicianLastVisitMonthName')?.markAsTouched();
    }
    if(this.physicianInfoForm.contains('physicianLastVisitYearName')){
      this.physicianInfoForm.get('physicianLastVisitYearName')?.markAsTouched();
    }

    this.physicianInfoForm.updateValueAndValidity();
    
    // Trigger change detection to ensure validation errors display immediately
    // This is particularly important for Firefox which may require explicit detection
    this.cdr.detectChanges();
  }

  public resetPhysicianError(){
    if(this.physicianErrorThrown && this.physicianInfoForm.controls['hasPhysician'].value === null){
      this.physicianInfoForm.controls['hasPhysician'].setValidators([Validators.required]);
      this.physicianInfoForm.controls['hasPhysician'].setErrors({'required': true});
      this.physicianInfoForm.updateValueAndValidity();
    }
  }

  public throwPhysicianError(){
    // Set flag to prevent showing modal multiple times in the same component visit
    this.physicianErrorThrown = true;
    // Save "none" selection BEFORE clearing the form and showing the modal
    // This ensures the selection is preserved if user navigates away without closing modal
    const formValue = { hasPhysician: 'none' };
    this.userSessionService.data['physicianFormData'] = formValue;
    //clear the form
    this.physicianInfoForm.controls['hasPhysician'].setValue(null);
    this.physicianInfoForm.controls['hasPhysician'].setErrors(null);
    this.physicianInfoForm.updateValueAndValidity();

    this.modalContentHeading = this.physicianModalContent.title;
    this.modalContentBody = FormUtils.sanitize(this.physicianModalContent.body);

    // Stop the spinner when showing the physician error modal
    this.formSubmitted = false;

    if(this.modalService){
      this.modalService.showModal('prism-ui-modal-physician-modal'); // Use the service to show the modal
    }
  }

  public closePhysicianModal(){
    // Mark modal as acknowledged in the session so it won't show again
    this.userSessionService.data['physicianModalAcknowledged'] = true;
    // Restore the form value back to 'none' so user doesn't have to reselect
    // (The "none" value was already saved before the modal was shown)
    this.physicianInfoForm.controls['hasPhysician'].setValue('none');
    this.physicianInfoForm.updateValueAndValidity();
    this.modalService.closeModal('prism-ui-modal-physician-modal');
  }

  sendPhysicianAnalytics(){
    if(this.physicianErrorThrown) {
      if(this.physicianInfoForm.controls['hasPhysician'].value == 'physician'){
        this.googleAnalyticsService.sendCustomEvent(this.physicianInfoProvidedAfterPrompt);
      } else if(this.physicianInfoForm.controls['hasPhysician'].value == 'facility'){
        this.googleAnalyticsService.sendCustomEvent(this.facilityInfoProvidedAfterPrompt);
      } else if(this.physicianInfoForm.controls['hasPhysician'].value == 'none'){
        this.googleAnalyticsService.sendCustomEvent(this.noInfoProvidedAfterPrompt);
      }
    } else {
      if(this.physicianInfoForm.controls['hasPhysician'].value == 'physician'){
        this.googleAnalyticsService.sendCustomEvent(this.physicianInfoProvided);
      } else if(this.physicianInfoForm.controls['hasPhysician'].value == 'facility'){
        this.googleAnalyticsService.sendCustomEvent(this.facilityInfoProvided);
      } else if(this.physicianInfoForm.controls['hasPhysician'].value == 'none'){
        this.googleAnalyticsService.sendCustomEvent(this.noInfoProvided);
      }
    }
  }

  logPhysicianRadioChangeEvent(controlName: string){
    this.physicianInfoForm.get('hasPhysician').setValue(controlName);

    if(this.physicianInfoForm.get('hasPhysician').value == '' || this.physicianInfoForm.get('hasPhysician').value == null){
      this.isPhysicianValid = false;
    } else {
      this.isPhysicianValid = true;
    }

  }

  fillPhysicianInfo(){
    let physician = new PhysicianMhs();

    if(this.userSessionService.data.mhsApplication != undefined){
      physician.hasPhysician = this.physicianInfoForm.controls['hasPhysician'].value;

      if(this.physicianInfoForm.controls['hasPhysician'].value == 'physician'){

        physician.physicianFirstName = this.physicianInfoForm.controls['physicianFirstName'].value;
        physician.physicianLastName = this.physicianInfoForm.controls['physicianLastName'].value;

      } else if(this.physicianInfoForm.controls['hasPhysician'].value == 'facility'){

        physician.medicalFacility = this.physicianInfoForm.controls['medicalFacility'].value;

      }

      if(this.physicianInfoForm.controls['hasPhysician'].value != 'none'){

        physician.phoneNumber = this.physicianInfoForm.controls['phoneNumber'].value;

        let address = new AddressMhs();

        address.line1 = this.physicianInfoForm.controls['physicianLine1'].value;
        address.city = this.physicianInfoForm.controls['city'].value;
        address.state = this.physicianInfoForm.controls['physicianState'].value;
        address.zip = this.physicianInfoForm.controls['physicianZipCode'].value;
        address.country = this.physicianInfoForm.controls['country'].value;

        physician.address = address;
        if(this.isGenericDynamicForm) {
          physician.lastConsultDate = this.physicianInfoForm.controls['lastConsultDate'].value;
          physician.lastConsultReason = null;
        } else {
          if(!this.isGenericDynamicForm){
            physician.lastConsultReason = this.physicianInfoForm.controls['lastConsultReason'].value;
          } else {
            physician.lastConsultReason = null;
          }

          physician.lastConsultDate = this.physicianInfoForm.get('lastConsultDateYear').value + '-' +
            FormUtils.padWithZero(this.physicianInfoForm.get('lastConsultDateMonth').value) + '-' +
            FormUtils.padWithZero(this.physicianInfoForm.get('lastConsultDateDay').value);
        }

      } else {
        physician.phoneNumber = null;

        let address = new AddressMhs();

        address.line1 = null;
        address.city = null;
        address.state = null;
        address.zip = null;
        address.country = null;

        physician.address = address;
        physician.lastConsultReason = null;
        physician.lastConsultDate = null;

      }

      this.userSessionService.data.mhsApplication.physician = physician;
    }
  }

  getPhysicianCountryLabel(optSelected: string){
    for(let country of this.countryList){
      if(country.abbreviation === optSelected){
        return country.label;
      }
    }
    return null;
  }

  //trim beginning and ending whitespace
  stateLeaveEvent(event){
    const inputValue = event.target.value;
    this.physicianInfoForm.get('physicianState').markAsTouched();
    this.physicianInfoForm.controls["physicianState"].setValue(inputValue.toString().trim());
    this.physicianInfoForm.updateValueAndValidity();
  }

  getStateLabel(optSelected: string){
    for(let state of this.stateRegionList){
      if(state.abbreviation === optSelected){
        return state.label;
      }
    }
    return null;
  }

  handleOptionSelectedPhysicianCountry(event: Event, key: string) {

    const customEvent = event as CustomEvent;
    let optSelected = customEvent.detail;
    let optValue = this.getPhysicianCountryValue(optSelected);
    this.physicianInfoForm.get(key).setValue(optValue);

    this.countryChange(optValue);

  }

  getPhysicianCountryValue(optSelected: string){
    for(let country of this.countryList){
      if(country.label === optSelected){
        return country.abbreviation;
      }
    }

    if(optSelected != null && optSelected != undefined && optSelected != ""){
      return this.countryList[0].abbreviation;
    } else {
      return null;
    }
  }

  // Event Handlers
  handleOptionSelectedState(event: Event, key: string) {

    const customEvent = event as CustomEvent;
    let optSelected = customEvent.detail;
    let optValue = this.getStateValue(optSelected);
    this.physicianInfoForm.get(key).setValue(optValue);
  }

  getStateValue(optSelected: string){
    for(let state of this.stateRegionList){
      if(state.label === optSelected){
        return state.abbreviation;
      }
    }

    if(optSelected != null && optSelected != undefined && optSelected != ""){
      return this.stateRegionList[0].abbreviation;
    } else {
      return null;
    }
  }

  //trim beginning and ending whitespace
  addressLine1LeaveEvent(event){
    const inputValue = event.target.value;
    this.physicianInfoForm.get('physicianLine1').markAsTouched();
    this.physicianInfoForm.controls["physicianLine1"].setValue(inputValue.toString().trim());
    this.physicianInfoForm.updateValueAndValidity();
  }

  //trim beginning and ending whitespace
  cityLeaveEvent(event){
    const inputValue = event.target.value;
    this.physicianInfoForm.get('city').markAsTouched();
    this.physicianInfoForm.controls["city"].setValue(inputValue.toString().trim());
    this.physicianInfoForm.updateValueAndValidity();
    this.genericLeaveEventValidation('city', this.maxLengthCity, this.NAME_PATTERN);  
  }

  //trim beginning and ending whitespace
  medicalFacilityLeaveEvent(event){
    const inputValue = event.target.value;
    this.physicianInfoForm.get('medicalFacility').markAsTouched();
    this.physicianInfoForm.controls["medicalFacility"].setValue(inputValue.toString().trim());
    this.physicianInfoForm.updateValueAndValidity();
  }

   //trim beginning and ending whitespace
   phoneLeaveEvent(event){
    const inputValue = event.target.value;
    this.physicianInfoForm.get('phoneNumber').markAsTouched();
    this.physicianInfoForm.controls["phoneNumber"].setValue(inputValue.toString().replace(/\D/g, ''));
    this.physicianInfoForm.updateValueAndValidity();
  }

  //trim beginning and ending whitespace
  physicianZipCodeLeaveEvent(event){
    const inputValue = event.target.value;
    this.physicianInfoForm.get('physicianZipCode').markAsTouched();
    this.physicianInfoForm.controls["physicianZipCode"].setValue(inputValue.toString().trim());
    this.physicianInfoForm.updateValueAndValidity();
  }

  //trim beginning and ending whitespace
  lastConsultReasonLeaveEvent(event){
    const inputValue = event.target.value;
    this.physicianInfoForm.get('lastConsultReason').markAsTouched();
    this.physicianInfoForm.controls["lastConsultReason"].setValue(inputValue.toString().trim());
    this.physicianInfoForm.updateValueAndValidity();
  }

  //trim beginning and ending whitespace
  firstNameLeaveEvent(){
    this.genericLeaveEventValidation('physicianFirstName', GlobalConstants.MAX_LENGTH_NAME, this.NAME_PATTERN);
  }

  //trim beginning and ending whitespace
  lastNameLeaveEvent(){
    this.genericLeaveEventValidation('physicianLastName', GlobalConstants.MAX_LENGTH_NAME, this.NAME_PATTERN);
  }

  genericLeaveEventValidation(controlName: string, maxLength: number, regExPattern: string){
    this.physicianInfoForm.get(controlName).markAsTouched();

    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.physicianInfoForm, controlName)){
      //maxLength
      let firstName = this.physicianInfoForm.get(controlName).value.trim();
      let firstNameLength = firstName ? firstName.length : 0;

      if(firstNameLength > maxLength){
        this.physicianInfoForm.get(controlName).setErrors({'maxlength':true});
        this.physicianInfoForm.updateValueAndValidity();
      } else {
        //pattern
        var pattern = new RegExp(regExPattern);
        const inputValue = this.physicianInfoForm.get(controlName).value;
        var patternBol = pattern.test(inputValue);

        if(patternBol){
          this.physicianInfoForm.get(controlName).setErrors(null);
          this.physicianInfoForm.updateValueAndValidity();
          return;
        } else {
          this.physicianInfoForm.get(controlName).setErrors({'pattern':true});
          this.physicianInfoForm.updateValueAndValidity();
        }
      }
      
    } else {
      //required 
      if(this.physicianInfoForm.get(controlName).touched){
        if(this.physicianInfoForm.get(controlName).value === null || 
           this.physicianInfoForm.get(controlName).value === '' ||
           this.physicianInfoForm.get(controlName).value === undefined){

            
            this.physicianInfoForm.get(controlName).setErrors({ required: true });
            this.physicianInfoForm.updateValueAndValidity();
        } 
      }
    }
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  onCancelModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log('info', 'Info log from PhysicianInfo.component', `Modal with id ${modalName} not found or doesn't support close method.`);
    }
  }

  // use the window service to access the modal element
  // and close the modal when the user clicks outside of it
  onClick(event: Event) {
    const modal = this.windowService.nativeDocument.getElementById(
      'prism-ui-modal-cancel-modal'
    );

    if (modal && event.target === modal) {
      this.onCancelModal('prism-ui-modal-cancel-modal');
    }
  }

  onCancel(){
    this.cancelEmitter.emit();
  }

  onBack(){
    // Ensure spinner is not showing when going back
    this.formSubmitted = false;
    // Force change detection to update the view immediately
    this.cdr.detectChanges();
    this.backEmitter.emit();
  }

  canDeactivate(){
    if (!this.submitted && !this.userSessionService.data.sessionEnded){
      this.modalContentHeading = this.cancelContent.title;
      this.modalContentBody = FormUtils.sanitize(this.cancelContent.body);

      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
      return this.userSessionService.data.sessionEnded;
    } else {
      return of(true);
    }
  }

  physicianConsultDateValid() {
    return this.physicianInfoForm.get('lastConsultDateMonth').valid &&
           this.physicianInfoForm.get('lastConsultDateDay').valid &&
           this.physicianInfoForm.get('lastConsultDateYear').valid;
  }

  lastConsultDateMonthLeaveEvent() {
    this.physicianInfoForm.get('lastConsultDateMonth').markAsTouched();
    this.physicianInfoForm.get('lastConsultDateMonth').updateValueAndValidity();

    //validate month
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.physicianInfoForm, 'lastConsultDateMonth')){
      if(this.formControlUtilitiesService.checkIfMonthControlIsBetweenOneAndTwelve(this.physicianInfoForm, 'lastConsultDateMonth')){
        this.physicianInfoForm.get('lastConsultDateMonth').setErrors(null);
      } else {
        this.physicianInfoForm.get('lastConsultDateMonth').setErrors({ dateError: true });
        return;
      }
    } else {
      this.physicianInfoForm.get('lastConsultDateMonth').setErrors({ dateError: true });
      return;
    }

    this.islastConsultDateFutureDated();
  }

  lastConsultDateDayLeaveEvent() {
    this.physicianInfoForm.get('lastConsultDateDay').markAsTouched();
    this.physicianInfoForm.get('lastConsultDateDay').updateValueAndValidity();

    //validate day
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.physicianInfoForm, 'lastConsultDateDay')){
      if(this.formControlUtilitiesService.checkIfDayControlIsBetweenOneAndThirtyOne(this.physicianInfoForm, 'lastConsultDateDay')) { 
        this.physicianInfoForm.get('lastConsultDateDay').setErrors(null);
      } else {
        this.physicianInfoForm.get('lastConsultDateDay').setErrors({ dateError: true });
        return;
      }
    } else {
      this.physicianInfoForm.get('lastConsultDateDay').setErrors({ dateError: true });
      return;
    }

    this.islastConsultDateFutureDated();
  }

  lastConsultDateYearLeaveEvent() {
    this.physicianInfoForm.get('lastConsultDateYear').markAsTouched();
    this.physicianInfoForm.get('lastConsultDateYear').updateValueAndValidity();

    //validate year
    if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.physicianInfoForm, 'lastConsultDateYear')) {
      if(this.formControlUtilitiesService.checkIfYearControlIsValid(this.physicianInfoForm, 'lastConsultDateYear')) {
        this.physicianInfoForm.get('lastConsultDateYear').setErrors(null);
      } else {
        this.physicianInfoForm.get('lastConsultDateYear').setErrors({ dateError: true });
        return;
      }
    } else {
      this.physicianInfoForm.get('lastConsultDateYear').setErrors({ dateError: true });
      return;
    }

    this.islastConsultDateFutureDated();
  }

  //can't future date last consult date
  islastConsultDateFutureDated(){
    let today = new Date();
    let tempDate = new Date(this.physicianInfoForm.get('lastConsultDateYear').value, this.physicianInfoForm.get('lastConsultDateMonth').value -1, this.physicianInfoForm.get('lastConsultDateDay').value);
    if(tempDate > today){
      this.physicianInfoForm.get('lastConsultDateMonth').setErrors({ dateError: true });
      this.physicianInfoForm.get('lastConsultDateDay').setErrors({ dateError: true });
      this.physicianInfoForm.get('lastConsultDateYear').setErrors({ dateError: true });
    } else {
      if(this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.physicianInfoForm, 'lastConsultDateYear') &&
         this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.physicianInfoForm, 'lastConsultDateMonth') &&
         this.formControlUtilitiesService.checkIfControlIsTouchedAndNotNullOrEmpty(this.physicianInfoForm, 'lastConsultDateDay') &&
         this.formControlUtilitiesService.checkIfYearControlIsValid(this.physicianInfoForm, 'lastConsultDateYear') &&
         this.formControlUtilitiesService.checkIfDayControlIsBetweenOneAndThirtyOne(this.physicianInfoForm, 'lastConsultDateDay') &&
         this.formControlUtilitiesService.checkIfMonthControlIsBetweenOneAndTwelve(this.physicianInfoForm, 'lastConsultDateMonth')){

        this.physicianInfoForm.get('lastConsultDateMonth').setErrors(null);
        this.physicianInfoForm.get('lastConsultDateDay').setErrors(null);
        this.physicianInfoForm.get('lastConsultDateYear').setErrors(null);
      }
    }
  }

  /**
   * Edit-mode "Back" button handler. Marks the navigation as safe and returns
   * to /review without saving any pending changes.
   */
  goBack(): void {
    if (this.isSavingEdit) {
      return;
    }
    // Discard any unsaved changes — do not overwrite the session cache.
    this.discardFormDataOnDestroy = true;
    // Mark as a safe navigation so the cancel-modal / leave-anyway flow is
    // bypassed when the deactivate guard fires.
    this.submitted = true;
    this.router.navigate([EEOI_APP_ROUTES.AUTH_ELEC_SIGN]);
  }

  /**
   * Edit-mode "Save" button handler. Validates the form and, if valid,
   * fills the physician model, persists it to the backend, and navigates
   * back to /review with the success toast.
   */
  saveAndGoToReview(): void {
    // Defend against a double-click while a save is already in flight.
    if (this.isSavingEdit) {
      return;
    }

    // Check if hasPhysician is set before doing anything
    const hasPhysicianValue = this.physicianInfoForm.controls['hasPhysician']?.value;
    if (!hasPhysicianValue || hasPhysicianValue === null || hasPhysicianValue === undefined || hasPhysicianValue === '') {
      this.physicianInfoForm.markAllAsTouched();
      this.physicianInfoForm.updateValueAndValidity();
      return;
    }

    this.touchAllControls();
    if(this.isGenericDynamicForm){
      this.setLastConsultDate();
    }
    
    let isFormValid = false;
    if(this.physicianInfoForm.controls['hasPhysician'].value != 'none'){
      isFormValid = this.physicianInfoForm.valid;
    } else {
      isFormValid = this.physicianInfoForm.valid;
    }

    if (!isFormValid) {
      this.scrollToFirstInvalidControl();
      return;
    }

    // Set saving state BEFORE calling save to show button spinner
    this.isSavingEdit = true;
    this.cdr.detectChanges();
    
    const spinnerStartTime = new Date().getTime();
    
    this.appInsightsLoggerService.log('info', 'PhysicianInfoComponent: Edit-mode save started.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');

    // Push the form values into the session-level physician model
    this.fillPhysicianInfo();
    this.sendPhysicianAnalytics();
    
    // Save to backend
    this.savePartialApplication(spinnerStartTime);
  }

  /**
   * Saves physician data in edit mode. Separate from savePhysician
   * to handle the edit-mode navigation flow.
   */
  async savePartialApplication(spinnerStartTime: number) {
    let resp = new PartialSaveResp();
    
    try {
      const result = await this.applicationService.updatePhysicianWithExStreamCloudApplication(
        this.userSessionService.data.mhsId,
        this.userSessionService.data.mhsApplication.physician
      );
      resp = result;
      this.appInsightsLoggerService.log('info', 'Partial application saved successfully. Component: PhysicianInfoComponent' + ' mhsId/caseId: ' + result.data.mhsId, '');
      
      // 201 (Created) and 304 (Not Modified) are both successful responses
      if (resp.httpStatusCode === 201 || resp.httpStatusCode === 304) {
        this.submitted = true;
        this.isPhysicianValid = true;
        this.isSavingEdit = false;
        this.formSubmitted = false;
        this.cdr.detectChanges();
        this.navigateBackToReviewAfterSave();
        
        this.appInsightsLoggerService.log('info', 'PhysicianInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'PhysicianInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      } else {
        this.appInsightsLoggerService.log('error', 'Physician Info updated with error. mhsId/caseId: ' + resp.data.mhsId, '');
        this.submitted = false;
        this.formSubmitted = false;
        this.isSavingEdit = false;
        this.cdr.detectChanges();
        this.appInsightsLoggerService.log('info', 'PhysicianInfoComponent: Spinner Stopped.' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
        const spinnerStopTime = new Date().getTime();
        const spinnerDuration = spinnerStopTime - spinnerStartTime;
        this.appInsightsLoggerService.log('info', 'PhysicianInfoComponent: Spinner Duration: ' + spinnerDuration + 'ms' + ' mhsId/caseId: ' + this.userSessionService.data.mhsId, '');
      }
    } catch (error) {
      this.appInsightsLoggerService.log('error', 'Physician Info edit-mode save failed: ' + error, '');
      if (error?.error?.data) {
        this.findInvalidControls(error.error.data);
        this.scrollToFirstInvalidControl();
      }
      this.submitted = false;
      this.formSubmitted = false;
      this.isSavingEdit = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Common post-save navigation: flip the success-toast flag and route back
   * to /review. Mirrors the applicant-info and med-history pages so
   * the user sees the same success banner regardless of which section they edited.
   */
  private navigateBackToReviewAfterSave(): void {
    this.userSessionService.data['showCoverageSavedToast'] = true;
    sessionStorage.setItem('showCoverageSavedToast', 'true');

    const destinationRoute = EEOI_APP_ROUTES.AUTH_ELEC_SIGN;
    this.submitted = true;
    this.router.navigate([destinationRoute], { state: { showSuccess: true } });
    this.appInsightsApiService.startNavigationEvent(destinationRoute.toString());
  }
}
